<script setup lang="ts">
import type { AttachmentUpload, CaseListItem } from "@maimu/contracts";
import { ATTACHMENT_ACCEPT, ATTACHMENT_MAX_BYTES, ATTACHMENT_MAX_COUNT } from "@maimu/contracts";
import { nextTick, onMounted, ref, watch } from "vue";

import { useChat } from "@/composables/useChat.js";
import { api } from "@/lib/api.js";

import MessageCard from "./MessageCard.vue";

/** A request from the toimik view to start a thread inside it (D21). */
const props = defineProps<{ caseRequest?: { caseId: string; n: number } | null }>();
const emit = defineEmits<{ (event: "cleared"): void }>();

const chat = useChat();
const caseTitle = ref("");

/**
 * Filing an existing conversation (D22). People rarely know at the first message
 * whether a discussion is going to become a toimik, so the decision belongs here,
 * after the fact, rather than only at the moment a thread is started.
 */
const myCases = ref<CaseListItem[]>([]);
const filing = ref(false);
const filePick = ref("");
const newCaseTitle = ref("");
const fileError = ref("");

async function openFiling(): Promise<void> {
  filing.value = !filing.value;
  fileError.value = "";
  if (filing.value) await loadMyCases();
}

async function fileIntoCase(): Promise<void> {
  const conversationId = chat.conversationId.value;
  if (!conversationId) return;

  fileError.value = "";
  try {
    if (filePick.value) {
      const kase = await api.attachConversationToCase(filePick.value, conversationId);
      chat.caseId.value = kase.id;
      chat.caseTitle.value = kase.title;
    } else if (newCaseTitle.value.trim()) {
      const created = await api.createCase({ title: newCaseTitle.value.trim() });
      await api.attachConversationToCase(created.id, conversationId);
      chat.caseId.value = created.id;
      chat.caseTitle.value = created.title;
      myCases.value = [];
    } else {
      return;
    }
    caseTitle.value = chat.caseTitle.value ?? "";
    filing.value = false;
    filePick.value = "";
    newCaseTitle.value = "";
  } catch (cause) {
    fileError.value = cause instanceof Error ? cause.message : "Toimikusse lisamine ebaõnnestus.";
  }
}

async function unfile(): Promise<void> {
  const conversationId = chat.conversationId.value;
  const caseId = chat.caseId.value;
  if (!conversationId || !caseId) return;
  if (!confirm("Eemaldada see vestlus toimikust? Toimiku liikmed ei näe seda enam.")) return;

  try {
    await api.detachConversationFromCase(caseId, conversationId);
    chat.caseId.value = null;
    chat.caseTitle.value = null;
    caseTitle.value = "";
  } catch (cause) {
    fileError.value = cause instanceof Error ? cause.message : "Eemaldamine ebaõnnestus.";
  }
}

/**
 * Opening a chat from a toimik starts a fresh thread bound to it. Continuing an
 * existing conversation never changes its toimik, so the binding is set here once
 * and cleared the moment the user starts an ordinary conversation.
 */
watch(
  () => props.caseRequest?.n,
  async () => {
    const caseId = props.caseRequest?.caseId;
    if (!caseId) return;
    chat.startNew();
    chat.caseId.value = caseId;
    try {
      const kase = await api.case(caseId);
      caseTitle.value = kase.title;
      chat.caseTitle.value = kase.title;
    } catch {
      caseTitle.value = "";
    }
  },
  { immediate: true },
);

/** Loaded once so both the up-front picker and the after-the-fact filing control
 *  have something to offer without a round trip per click. */
async function loadMyCases(): Promise<void> {
  if (myCases.value.length) return;
  try {
    myCases.value = (await api.cases()).filter((item) => item.member && item.status !== "archived");
  } catch (cause) {
    fileError.value = cause instanceof Error ? cause.message : "Toimikuid ei õnnestunud laadida.";
  }
}

/** Choosing a toimik before the first message: the conversation is created inside it
 *  rather than moved into it afterwards. */
function pickCase(id: string): void {
  const chosen = myCases.value.find((item) => item.id === id) ?? null;
  chat.caseId.value = chosen?.id ?? null;
  chat.caseTitle.value = chosen?.title ?? null;
  caseTitle.value = chosen?.title ?? "";
  if (!chosen) emit("cleared");
}

function leaveCase(): void {
  chat.caseId.value = null;
  caseTitle.value = "";
  chat.startNew();
  emit("cleared");
}
const draft = ref("");
const pending = ref<AttachmentUpload[]>([]);
const attachError = ref("");
const fileInput = ref<HTMLInputElement | null>(null);
const scroller = ref<HTMLElement | null>(null);

/** Base64 in the JSON body, exactly as Phase 1 did (reference §10). */
const toDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Faili lugemine ebaõnnestus."));
    reader.readAsDataURL(file);
  });

async function onFiles(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  attachError.value = "";

  for (const file of Array.from(input.files ?? [])) {
    if (pending.value.length >= ATTACHMENT_MAX_COUNT) {
      attachError.value = "Korraga saab lisada kuni 5 manust.";
      break;
    }
    // Checked here as well as on the server, so the user hears about it before
    // uploading eight megabytes of base64.
    if (file.size > ATTACHMENT_MAX_BYTES) {
      attachError.value = `${file.name} on liiga suur. Piir on 8 MB.`;
      continue;
    }
    pending.value.push({ name: file.name, dataUrl: await toDataUrl(file) });
  }

  input.value = "";
}

const removeAttachment = (index: number): void => void pending.value.splice(index, 1);

onMounted(() => {
  void chat.refreshConversations();
  // Eagerly, not on focus: a select whose options arrive after the click opens empty,
  // and the user concludes they have no toimikud.
  void loadMyCases();
});

watch(
  () => chat.turns.value.map((turn) => turn.content).join("").length,
  async () => {
    await nextTick();
    const element = scroller.value;
    if (element) element.scrollTop = element.scrollHeight;
  },
);

async function submit(): Promise<void> {
  const text = draft.value;
  if (!text.trim() || chat.streaming.value) return;

  const files = pending.value;
  draft.value = "";
  pending.value = [];
  attachError.value = "";
  await chat.send(text, files);
}

/** Enter sends, Shift+Enter makes a new line. */
function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    void submit();
  }
}
</script>

<template>
  <div class="layout">
    <aside class="sidebar">
      <button class="btn-primary new" type="button" @click="leaveCase()">Uus vestlus</button>

      <!-- Stated rather than implied: what you type here lands in a shared toimik,
           and every member can read it. -->
      <div v-if="chat.caseId.value" class="incase">
        <p class="incase-title">Toimikus: {{ chat.caseTitle.value || caseTitle || "…" }}</p>
        <p class="incase-note">
          {{
            chat.conversationId.value
              ? "Vestlus on toimiku liikmetele nähtav ja algab toimiku kokkuvõttest."
              : "Esimene sõnum loob vestluse selle toimiku sees."
          }}
        </p>
        <button v-if="chat.conversationId.value" class="linkish" type="button" @click="unfile()">
          Eemalda toimikust
        </button>
        <button v-else class="linkish" type="button" @click="pickCase('')">Alusta ilma toimikuta</button>
      </div>

      <!-- Before the first message the toimik is a choice, not a move: picking one
           here creates the conversation inside it. -->
      <div v-else-if="!chat.conversationId.value" class="incase plain">
        <label class="pick">
          <span>Toimik (valikuline)</span>
          <select :value="chat.caseId.value ?? ''" @change="pickCase(($event.target as HTMLSelectElement).value)">
            <option value="">Ilma toimikuta</option>
            <option v-for="item in myCases" :key="item.id" :value="item.id">
              {{ item.code ? `${item.code} · ` : "" }}{{ item.title }}
            </option>
          </select>
        </label>
        <p v-if="fileError" class="incase-error">{{ fileError }}</p>
      </div>

      <!-- Once a conversation exists the same decision becomes a move. -->
      <div v-else class="incase plain">
        <button class="linkish" type="button" @click="openFiling()">
          {{ filing ? "Loobu" : "Lisa see vestlus toimikusse" }}
        </button>
        <template v-if="filing">
          <select v-model="filePick">
            <option value="">Uus toimik…</option>
            <option v-for="item in myCases" :key="item.id" :value="item.id">
              {{ item.code ? `${item.code} · ` : "" }}{{ item.title }}
            </option>
          </select>
          <input v-if="!filePick" v-model.trim="newCaseTitle" type="text" placeholder="Uue toimiku pealkiri" />
          <button class="btn" type="button" :disabled="!filePick && !newCaseTitle.trim()" @click="fileIntoCase()">
            Lisa
          </button>
          <p v-if="fileError" class="incase-error">{{ fileError }}</p>
        </template>
      </div>

      <p class="heading">Vestlused</p>
      <ul v-if="chat.conversations.value.length" class="list">
        <li v-for="conversation in chat.conversations.value" :key="conversation.id">
          <button
            type="button"
            class="item"
            :class="{ active: conversation.id === chat.conversationId.value }"
            @click="chat.open(conversation.id)"
          >
            {{ conversation.title }}
          </button>
        </li>
      </ul>
      <p v-else class="empty">Vestlusi veel ei ole.</p>
    </aside>

    <main class="main">
      <div ref="scroller" class="stream">
        <div v-if="!chat.turns.value.length" class="intro card">
          <h2>Küsi assistendilt</h2>
          <p>mAImu vaatab küsimuse peale ise JAHK andmebaasi, Shopify seisu ja Directo laoseisu. Küsi näiteks:</p>
          <ul>
            <li>Miks toote 523-06000 lapstooted ei tule Shopifysse?</li>
            <li>Mis on 523-04000 laoseis Tartus?</li>
            <li>Näita toote 410-01200 sünkiajalugu.</li>
          </ul>
          <p class="footnote">
            Assistent ainult loeb andmeid — ta ei käivita sünki ega paranda välju. Ebakindlad küsimused lähevad
            inimesele vastamiseks.
          </p>
        </div>

        <MessageCard v-for="turn in chat.turns.value" :key="turn.id" :turn="turn" />
      </div>

      <form class="composer" @submit.prevent="submit">
        <p v-if="chat.status.value" class="status">{{ chat.status.value }}</p>
        <p v-else-if="chat.error.value" class="status error">{{ chat.error.value }}</p>

        <textarea
          v-model="draft"
          rows="3"
          placeholder="Näiteks: miks toote 523-06000 lapstooted ei jõua Shopifysse?"
          :disabled="chat.streaming.value"
          @keydown="onKeydown"
        />

        <ul v-if="pending.length" class="attachments">
          <li v-for="(file, index) in pending" :key="`${file.name}-${index}`">
            {{ file.name }}
            <button type="button" aria-label="Eemalda" @click="removeAttachment(index)">×</button>
          </li>
        </ul>
        <p v-if="attachError" class="status error">{{ attachError }}</p>

        <div class="actions">
          <input
            ref="fileInput"
            class="file"
            type="file"
            multiple
            :accept="ATTACHMENT_ACCEPT"
            :disabled="chat.streaming.value"
            @change="onFiles"
          />
          <button class="btn-ghost" type="button" :disabled="chat.streaming.value" @click="fileInput?.click()">
            Lisa screenshot või fail
          </button>
          <span class="hint">Enter saadab, Shift+Enter teeb uue rea.</span>
          <button v-if="chat.streaming.value" class="btn-ghost" type="button" @click="chat.stop()">Katkesta</button>
          <button class="btn-primary" type="submit" :disabled="chat.streaming.value || !draft.trim()">
            Küsi assistendilt
          </button>
        </div>
      </form>
    </main>
  </div>
</template>

<style scoped>
.layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem;
  height: calc(100vh - 78px);
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-height: 0;
}

.new {
  width: 100%;
}

.incase {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  border: 1px solid var(--green);
  border-radius: 10px;
  padding: 0.45rem 0.6rem;
  background: var(--soft);
}

.incase-title {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--green);
}

.incase-note {
  margin: 0.2rem 0 0;
  font-size: 0.72rem;
  color: var(--muted);
}

.incase.plain {
  border-color: var(--line);
  background: transparent;
}

.incase select,
.incase input {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.35rem 0.5rem;
  font: inherit;
  font-size: 0.8rem;
  background: #fff;
  color: var(--ink);
}

.pick {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.pick > span {
  font-size: 0.72rem;
  color: var(--muted);
}

.incase-error {
  margin: 0;
  font-size: 0.72rem;
  color: var(--warn);
}

.linkish {
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.74rem;
  color: var(--green);
  cursor: pointer;
  text-decoration: underline;
  text-align: left;
}

.heading {
  margin: 0.5rem 0 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.item {
  width: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  padding: 0.45rem 0.6rem;
  font-size: 0.85rem;
  color: var(--steel);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item:hover {
  background: var(--soft);
}

.item.active {
  background: var(--card);
  border-color: var(--line);
  font-weight: 600;
}

.empty {
  margin: 0;
  font-size: 0.82rem;
  color: var(--muted);
}

.main {
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 0.75rem;
}

.stream {
  flex: 1;
  overflow-y: auto;
  padding-right: 0.4rem;
}

.intro {
  padding: 1.25rem;
  max-width: 46rem;
}

.intro h2 {
  margin: 0 0 0.5rem;
  font-size: 1.15rem;
}

.intro p {
  margin: 0 0 0.6rem;
  color: var(--steel);
}

.intro ul {
  margin: 0 0 0.8rem;
  padding-left: 1.2rem;
  color: var(--muted);
}

.footnote {
  margin: 0;
  font-size: 0.82rem;
  color: var(--muted);
}

.file {
  display: none;
}

.attachments {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.attachments li {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  background: var(--soft);
}

.attachments button {
  border: 0;
  background: none;
  cursor: pointer;
  color: var(--muted);
  font-size: 0.9rem;
  line-height: 1;
  padding: 0;
}

.composer {
  border-top: 1px solid var(--line);
  padding-top: 0.75rem;
}

.status {
  margin: 0 0 0.4rem;
  font-size: 0.82rem;
  color: var(--gold-dark);
}

.status.error {
  color: var(--warn);
}

.actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.6rem;
}

.hint {
  flex: 1;
  font-size: 0.78rem;
  color: var(--muted);
}

@media (max-width: 860px) {
  .layout {
    grid-template-columns: 1fr;
    height: auto;
  }

  .sidebar {
    order: 2;
  }
}
</style>
